/**
 * Organization Branding Settings
 * Allows admins to customize their tenant's visual branding
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../config/api';
import { 
  Palette, 
  Upload, 
  Image as ImageIcon, 
  Eye, 
  Check, 
  Loader2, 
  RefreshCw, 
  Layout, 
  Megaphone, 
  Layers, 
  Sparkles,
  Brain,
  AlertTriangle,
  GraduationCap,
  Wand2,
  RotateCcw
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
const API = API_BASE;

// Quick palette themes for 1-click branding setup
const PALETTE_PRESETS = [
  {
    name: 'Old Mutual Green & Lime',
    primary: '#04AF37',
    secondary: '#84cc16',
    accent: '#10b981',
    heading: '#ffffff',
    text: '#cbd5e1'
  },
  {
    name: 'Cerebro Gold & Amber',
    primary: '#D4AF37',
    secondary: '#f59e0b',
    accent: '#0d9488',
    heading: '#ffffff',
    text: '#94a3b8'
  },
  {
    name: 'Corporate Navy & Sky',
    primary: '#1e40af',
    secondary: '#0284c7',
    accent: '#06b6d4',
    heading: '#ffffff',
    text: '#cbd5e1'
  },
  {
    name: 'Forest Green & Gold',
    primary: '#166534',
    secondary: '#eab308',
    accent: '#14b8a6',
    heading: '#ffffff',
    text: '#cbd5e1'
  },
  {
    name: 'Royal Purple & Fuchsia',
    primary: '#7c3aed',
    secondary: '#c026d3',
    accent: '#ec4899',
    heading: '#ffffff',
    text: '#cbd5e1'
  },
  {
    name: 'Sunset Orange & Amber',
    primary: '#ea580c',
    secondary: '#f59e0b',
    accent: '#e11d48',
    heading: '#ffffff',
    text: '#cbd5e1'
  },
  {
    name: 'Slate Gray & Cyan',
    primary: '#475569',
    secondary: '#64748b',
    accent: '#0ea5e9',
    heading: '#ffffff',
    text: '#94a3b8'
  }
];

// Swatches for individual color pickers
const COLOR_SWATCHES = [
  { name: 'Cerebro Gold', color: '#D4AF37' },
  { name: 'Amber Gold', color: '#f59e0b' },
  { name: 'Old Mutual Green', color: '#04AF37' },
  { name: 'Lime Green', color: '#84cc16' },
  { name: 'Emerald', color: '#10B981' },
  { name: 'Teal', color: '#0d9488' },
  { name: 'Sky Blue', color: '#0284c7' },
  { name: 'Corporate Blue', color: '#1e40af' },
  { name: 'Royal Purple', color: '#7c3aed' },
  { name: 'Fuchsia', color: '#c026d3' },
  { name: 'Sunset Orange', color: '#ea580c' },
  { name: 'Rose Red', color: '#e11d48' },
  { name: 'White', color: '#ffffff' },
  { name: 'Light Slate', color: '#cbd5e1' },
  { name: 'Muted Slate', color: '#94a3b8' },
  { name: 'Dark Slate', color: '#475569' }
];

// Reusable color picker row component
function ColorControl({ label, description, value, onChange }) {
  return (
    <div className="p-4 bg-zinc-900/60 border border-zinc-800 rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-white text-sm font-semibold block">{label}</Label>
          {description && <p className="text-xs text-zinc-400 mt-0.5">{description}</p>}
        </div>
        <div 
          className="w-7 h-7 rounded-md border border-zinc-700 shadow-inner flex-shrink-0"
          style={{ backgroundColor: value }}
        />
      </div>

      {/* Quick Swatches */}
      <div className="flex flex-wrap gap-1.5">
        {COLOR_SWATCHES.map((preset) => (
          <button
            key={preset.color}
            type="button"
            onClick={() => onChange(preset.color)}
            className={`w-5 h-5 rounded-full border transition-all ${
              value.toLowerCase() === preset.color.toLowerCase() 
                ? 'border-white scale-110 shadow-sm ring-1 ring-white/50' 
                : 'border-transparent hover:scale-105 opacity-80 hover:opacity-100'
            }`}
            style={{ backgroundColor: preset.color }}
            title={preset.name}
          />
        ))}
      </div>

      {/* Hex Input and Native Color Picker */}
      <div className="flex items-center gap-2 pt-1">
        <Input
          type="text"
          value={value}
          onChange={(e) => {
            const val = e.target.value;
            if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
              onChange(val);
            }
          }}
          placeholder="#000000"
          className="bg-zinc-800 border-zinc-700 text-white font-mono text-xs uppercase h-8 max-w-[130px]"
        />
        <label className="cursor-pointer relative flex items-center justify-center h-8 px-3 rounded-md bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 transition-colors">
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full h-full cursor-pointer bg-transparent border-0 opacity-0 absolute inset-0"
          />
          <span className="text-xs text-zinc-300 flex items-center gap-1.5">
            <span 
              className="w-3.5 h-3.5 rounded border border-zinc-600 inline-block"
              style={{ backgroundColor: value }}
            />
            Picker
          </span>
        </label>
      </div>
    </div>
  );
}

export default function BrandingSettings({ token, organizationId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  
  const [branding, setBranding] = useState({
    name: '',
    logo_url: null,
    primary_color: '#D4AF37',
    secondary_color: '#f59e0b',
    accent_color: '#0d9488',
    heading_color: '#ffffff',
    text_color: '#94a3b8',
    favicon_url: null,
    login_background_url: null
  });

  const [landingPage, setLandingPage] = useState({
    hero_title: '',
    hero_subtitle: '',
    badge_text: '',
    cta_text: '',
    cta_link: '',
    announcement_enabled: false,
    announcement_text: '',
    show_courses: true,
    show_labs: true,
    show_pathways: true,
    show_features: true,
    show_stats: true
  });
  
  const [colors, setColors] = useState({
    primary: '#D4AF37',
    secondary: '#f59e0b',
    accent: '#0d9488',
    heading: '#ffffff',
    text: '#94a3b8'
  });

  const normalizeLogoUrl = (url) => {
    if (!url) return null;
    if (url.includes('integrations.emergentagent.com/objstore/api/v1/storage/objects/')) {
      const remainder = url.split('integrations.emergentagent.com/objstore/api/v1/storage/objects/')[1];
      return `/api/public/files/${remainder}`;
    }
    return url;
  };

  useEffect(() => {
    fetchBranding();
  }, [organizationId]);

  const fetchBranding = async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/api/organizations/${organizationId}/branding`);
      const data = res.data || {};
      if (data.logo_url) {
        data.logo_url = normalizeLogoUrl(data.logo_url);
      }
      if (data.login_background_url) {
        data.login_background_url = normalizeLogoUrl(data.login_background_url);
      }
      setBranding(data);
      if (data.landing_page) {
        setLandingPage(prev => ({ ...prev, ...data.landing_page }));
      }
      setColors({
        primary: data.primary_color || '#D4AF37',
        secondary: data.secondary_color || '#f59e0b',
        accent: data.accent_color || '#0d9488',
        heading: data.heading_color || '#ffffff',
        text: data.text_color || '#94a3b8'
      });
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPEG, or WebP');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploadingBg(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(
        `${API}/api/organizations/${organizationId}/branding/background`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const bgUrl = normalizeLogoUrl(res.data.background_url);
      setBranding(prev => ({ ...prev, login_background_url: bgUrl }));
      toast.success('Background image uploaded successfully');
    } catch (error) {
      console.error('Failed to upload background:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload background image');
    } finally {
      setUploadingBg(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPEG, WebP, or SVG');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB');
      return;
    }
    
    setUploadingLogo(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(
        `${API}/api/organizations/${organizationId}/branding/logo`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const logoUrl = normalizeLogoUrl(res.data.logo_url);
      setBranding(prev => ({ ...prev, logo_url: logoUrl }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveBranding = async () => {
    setSaving(true);
    
    try {
      await axios.put(
        `${API}/api/organizations/${organizationId}/branding`,
        { 
          primary_color: colors.primary,
          secondary_color: colors.secondary,
          accent_color: colors.accent,
          heading_color: colors.heading,
          text_color: colors.text,
          landing_page: landingPage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBranding(prev => ({
        ...prev,
        primary_color: colors.primary,
        secondary_color: colors.secondary,
        accent_color: colors.accent,
        heading_color: colors.heading,
        text_color: colors.text,
        landing_page: landingPage
      }));
      toast.success('Branding and color settings saved successfully');
      
      // Apply to current document
      document.documentElement.style.setProperty('--tenant-primary', colors.primary);
      document.documentElement.style.setProperty('--tenant-secondary', colors.secondary);
      document.documentElement.style.setProperty('--tenant-accent', colors.accent);
      document.documentElement.style.setProperty('--tenant-heading', colors.heading);
      document.documentElement.style.setProperty('--tenant-text', colors.text);
    } catch (error) {
      console.error('Failed to save branding:', error);
      toast.error(error.response?.data?.detail || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const hasUnsavedChanges = 
    colors.primary !== (branding.primary_color || '#D4AF37') ||
    colors.secondary !== (branding.secondary_color || '#f59e0b') ||
    colors.accent !== (branding.accent_color || '#0d9488') ||
    colors.heading !== (branding.heading_color || '#ffffff') ||
    colors.text !== (branding.text_color || '#94a3b8');

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="p-6 bg-navigate-surface border border-zinc-800 rounded-xl">
        <p className="text-zinc-400 text-center">
          Please create or select an organization to configure branding.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2">
            <Palette className="h-5 w-5 text-navigate-gold" />
            Branding Settings
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            Customize your organization's visual appearance and color palette
          </p>
        </div>
        <Button
          onClick={handleSaveBranding}
          disabled={saving || !hasUnsavedChanges}
          className="bg-navigate-gold text-black hover:bg-navigate-gold-light"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left Column - Settings */}
        <div className="space-y-6">
          {/* Logo Upload */}
          <div className="p-6 bg-navigate-surface border border-zinc-800 rounded-xl">
            <Label className="text-white mb-4 block">Organization Logo</Label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center overflow-hidden">
                {branding.logo_url ? (
                  <img 
                    src={branding.logo_url} 
                    alt="Logo" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <ImageIcon className="h-8 w-8 text-zinc-600" />
                )}
              </div>
              <div className="flex-1">
                <label className="cursor-pointer">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors">
                    {uploadingLogo ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    onChange={handleLogoUpload}
                    disabled={uploadingLogo}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-zinc-500 mt-2">
                  PNG, JPEG, WebP or SVG. Max 2MB.
                </p>
              </div>
            </div>
          </div>

          {/* Background Image Upload */}
          <div className="p-6 bg-navigate-surface border border-zinc-800 rounded-xl">
            <Label className="text-white mb-4 block">Landing & Login Background Image</Label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-16 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center overflow-hidden">
                {branding.login_background_url ? (
                  <img 
                    src={branding.login_background_url} 
                    alt="Background" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon className="h-8 w-8 text-zinc-600" />
                )}
              </div>
              <div className="flex-1">
                <label className="cursor-pointer">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors">
                    {uploadingBg ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {uploadingBg ? 'Uploading...' : (branding.login_background_url ? 'Change Background' : 'Upload Background')}
                  </div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleBackgroundUpload}
                    disabled={uploadingBg}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-zinc-500 mt-2">
                  PNG, JPEG or WebP. Recommended 1920x1080. Max 5MB.
                </p>
              </div>
            </div>
          </div>

          {/* Color Customization Section */}
          <div className="p-6 bg-navigate-surface border border-zinc-800 rounded-xl space-y-5">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <div>
                <Label className="text-white text-base font-bold flex items-center gap-2">
                  <Palette className="h-4 w-4 text-navigate-gold" />
                  Color Customization
                </Label>
                <p className="text-xs text-zinc-400 mt-0.5">
                  Control all landing page and portal accent colours, headings, and copy
                </p>
              </div>
              <button
                type="button"
                onClick={() => setColors({
                  primary: '#D4AF37',
                  secondary: '#f59e0b',
                  accent: '#0d9488',
                  heading: '#ffffff',
                  text: '#94a3b8'
                })}
                className="text-xs text-zinc-400 hover:text-white flex items-center gap-1 transition-colors"
                title="Reset to defaults"
              >
                <RotateCcw className="h-3 w-3" />
                Reset
              </button>
            </div>

            {/* Quick 1-Click Palette Presets */}
            <div>
              <Label className="text-zinc-300 text-xs font-semibold mb-2 block flex items-center gap-1.5">
                <Wand2 className="h-3.5 w-3.5 text-navigate-gold" />
                Quick Brand Palettes
              </Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {PALETTE_PRESETS.map((p) => (
                  <button
                    key={p.name}
                    type="button"
                    onClick={() => setColors({
                      primary: p.primary,
                      secondary: p.secondary,
                      accent: p.accent,
                      heading: p.heading,
                      text: p.text
                    })}
                    className="flex items-center gap-2 p-2 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-all text-left text-xs group"
                  >
                    <div className="flex -space-x-1 flex-shrink-0">
                      <span className="w-3.5 h-3.5 rounded-full border border-black/50" style={{ backgroundColor: p.primary }} />
                      <span className="w-3.5 h-3.5 rounded-full border border-black/50" style={{ backgroundColor: p.secondary }} />
                      <span className="w-3.5 h-3.5 rounded-full border border-black/50" style={{ backgroundColor: p.accent }} />
                    </div>
                    <span className="text-zinc-300 truncate group-hover:text-white">{p.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Granular Color Controls */}
            <div className="space-y-3 pt-2">
              <ColorControl
                label="Primary Accent Colour"
                description="Main buttons, badges, brand links, and key highlights"
                value={colors.primary}
                onChange={(val) => setColors(prev => ({ ...prev, primary: val }))}
              />

              <ColorControl
                label="Secondary Accent Colour"
                description="Headline gradient end ('Learning Academy'), alert badges, warning highlights"
                value={colors.secondary}
                onChange={(val) => setColors(prev => ({ ...prev, secondary: val }))}
              />

              <ColorControl
                label="Feature & AI Accent Colour"
                description="AI tags, 'Intelligence Built Into Every Layer', tech icons, progress meters"
                value={colors.accent}
                onChange={(val) => setColors(prev => ({ ...prev, accent: val }))}
              />

              <ColorControl
                label="Heading Text Colour"
                description="Hero headline and major section titles"
                value={colors.heading}
                onChange={(val) => setColors(prev => ({ ...prev, heading: val }))}
              />

              <ColorControl
                label="Body & Subtitle Text Colour"
                description="Hero subtitle, paragraph descriptions, and card copy"
                value={colors.text}
                onChange={(val) => setColors(prev => ({ ...prev, text: val }))}
              />
            </div>
          </div>

          {/* Powered by Notice */}
          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <p className="text-sm text-amber-400">
              <strong>Note:</strong> The "Powered by Cerebro Core" badge will always be visible 
              on your tenant's login page and footer. This cannot be disabled.
            </p>
          </div>
        </div>

        {/* Right Column - Live Preview */}
        <div className="p-6 bg-navigate-surface border border-zinc-800 rounded-xl">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="h-5 w-5 text-zinc-400" />
            <Label className="text-white font-semibold">Live Preview</Label>
          </div>
          
          {/* Mini Portal & Landing Page Preview */}
          <div className="bg-slate-950 rounded-xl p-5 border border-zinc-800 space-y-5">
            {/* Header & Org Name */}
            <div className="flex items-center justify-between pb-3 border-b border-zinc-800">
              <div className="flex items-center gap-2.5">
                {branding.logo_url ? (
                  <img src={branding.logo_url} alt="Logo" className="h-7 w-7 object-contain" />
                ) : (
                  <div className="h-7 w-7 rounded bg-zinc-800 border border-zinc-700" />
                )}
                <span 
                  className="font-heading font-bold text-sm"
                  style={{ color: colors.primary }}
                >
                  {branding.name || 'Your Organization'}
                </span>
              </div>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-zinc-900 text-zinc-400 border border-zinc-800">
                Live Theming
              </span>
            </div>

            {/* Hero Headline Preview */}
            <div className="space-y-2 text-center pt-2">
              <div 
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs mx-auto border"
                style={{
                  backgroundColor: `${colors.accent}1A`,
                  borderColor: `${colors.accent}40`,
                  color: colors.accent
                }}
              >
                <Sparkles className="h-3 w-3" />
                <span>Official {branding.name || 'Organization'} Platform</span>
              </div>

              <h4 className="text-lg font-bold" style={{ color: colors.heading }}>
                Welcome to{' '}
                <span 
                  className="text-transparent bg-clip-text"
                  style={{ backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})` }}
                >
                  {branding.name || 'Organization'} Learning Academy
                </span>
              </h4>

              <p className="text-xs leading-relaxed max-w-sm mx-auto" style={{ color: colors.text }}>
                Accelerate your capabilities with compliant learning, simulations, and career pathways.
              </p>
            </div>

            {/* Platform Showcase & Alert Preview */}
            <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 space-y-4">
              <div className="flex items-center justify-between">
                <div 
                  className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border"
                  style={{
                    backgroundColor: `${colors.accent}1A`,
                    borderColor: `${colors.accent}40`,
                    color: colors.accent
                  }}
                >
                  <Brain className="h-3 w-3" />
                  <span>AI-Powered Platform</span>
                </div>
                <span className="text-xs font-semibold" style={{ color: colors.accent }}>87% Completed</span>
              </div>

              <div>
                <p className="text-xs font-bold" style={{ color: colors.heading }}>
                  Intelligence Built Into{' '}
                  <span style={{ color: colors.accent }}>Every Layer</span>
                </p>
                <div className="h-1.5 w-full bg-slate-800 rounded-full mt-2 overflow-hidden">
                  <div 
                    className="h-full w-[87%] rounded-full"
                    style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.accent})` }}
                  />
                </div>
              </div>

              {/* Regulatory Alert */}
              <div 
                className="p-2.5 rounded-lg flex items-center gap-2.5"
                style={{
                  backgroundColor: `${colors.secondary}15`,
                  borderColor: `${colors.secondary}40`,
                  borderWidth: '1px'
                }}
              >
                <AlertTriangle className="h-4 w-4 flex-shrink-0" style={{ color: colors.secondary }} />
                <div className="text-left text-[11px]">
                  <p className="font-semibold" style={{ color: colors.secondary }}>New Regulatory Alert</p>
                  <p style={{ color: colors.text }}>Updated AML & Compliance Guidance</p>
                </div>
              </div>
            </div>

            {/* Buttons & Links Preview */}
            <div className="pt-2 border-t border-zinc-800 space-y-2">
              <p className="text-[11px] text-zinc-500">Buttons & CTA Previews:</p>
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  className="flex-1 py-2 px-3 rounded-lg font-semibold text-xs text-slate-950 transition-all shadow"
                  style={{ backgroundColor: colors.primary }}
                >
                  Explore Courses
                </button>
                <button
                  type="button"
                  className="py-2 px-3 rounded-lg font-medium text-xs bg-slate-900 border border-slate-700 text-white"
                >
                  Sign In
                </button>
                <span 
                  className="text-xs cursor-pointer hover:underline"
                  style={{ color: colors.primary }}
                >
                  View Catalog →
                </span>
              </div>
            </div>
            
            <div className="pt-2 border-t border-zinc-800 flex items-center justify-center gap-1.5 text-[11px] text-zinc-500">
              <span>Powered by</span>
              <span className="font-semibold text-navigate-gold">Cerebro Core</span>
            </div>
          </div>
        </div>
      </div>

      {/* Landing Page Content & Controls Section */}
      <div className="p-6 bg-navigate-surface border border-zinc-800 rounded-xl space-y-6">
        <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Layout className="h-5 w-5 text-navigate-gold" />
              Landing Page Content & Controls
            </h3>
            <p className="text-sm text-zinc-400 mt-1">
              Customize the public landing page copy, call-to-actions, and section visibility for this tenant
            </p>
          </div>
          <Button 
            onClick={handleSaveBranding}
            disabled={saving}
            className="bg-navigate-gold hover:bg-navigate-gold/90 text-black font-semibold"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
            Save Landing Page Settings
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Copy & Text */}
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300 text-sm font-medium mb-1 block">Hero Headline</Label>
              <Input
                value={landingPage.hero_title || ''}
                onChange={(e) => setLandingPage(prev => ({ ...prev, hero_title: e.target.value }))}
                placeholder={`Welcome to ${branding.name || 'Organization'} Learning Academy`}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
              <p className="text-xs text-zinc-500 mt-1">Leave empty to use the default welcome title.</p>
            </div>

            <div>
              <Label className="text-zinc-300 text-sm font-medium mb-1 block">Hero Subtitle / Description</Label>
              <Textarea
                rows={3}
                value={landingPage.hero_subtitle || ''}
                onChange={(e) => setLandingPage(prev => ({ ...prev, hero_subtitle: e.target.value }))}
                placeholder="Accelerate your professional capabilities with enterprise compliance courses, interactive simulations, and structured career pathways designed for your team."
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            <div>
              <Label className="text-zinc-300 text-sm font-medium mb-1 block">Platform Badge Tagline</Label>
              <Input
                value={landingPage.badge_text || ''}
                onChange={(e) => setLandingPage(prev => ({ ...prev, badge_text: e.target.value }))}
                placeholder={`Official ${branding.name || 'Organization'} Learning Platform`}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300 text-sm font-medium mb-1 block">CTA Button Label</Label>
                <Input
                  value={landingPage.cta_text || ''}
                  onChange={(e) => setLandingPage(prev => ({ ...prev, cta_text: e.target.value }))}
                  placeholder="Explore Courses"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-300 text-sm font-medium mb-1 block">CTA Button Link</Label>
                <Input
                  value={landingPage.cta_link || ''}
                  onChange={(e) => setLandingPage(prev => ({ ...prev, cta_link: e.target.value }))}
                  placeholder="/courses"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>
          </div>

          {/* Right: Section Toggles & Announcement */}
          <div className="space-y-6">
            {/* Announcement Banner */}
            <div className="p-4 bg-zinc-900/60 border border-zinc-800 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Megaphone className="h-4 w-4 text-amber-400" />
                  <Label className="text-white text-sm font-medium">Top Announcement Banner</Label>
                </div>
                <Switch
                  checked={landingPage.announcement_enabled || false}
                  onCheckedChange={(val) => setLandingPage(prev => ({ ...prev, announcement_enabled: val }))}
                />
              </div>
              {landingPage.announcement_enabled && (
                <Input
                  value={landingPage.announcement_text || ''}
                  onChange={(e) => setLandingPage(prev => ({ ...prev, announcement_text: e.target.value }))}
                  placeholder="e.g. Mandatory Annual Compliance Training deadline: Nov 30"
                  className="bg-zinc-800 border-zinc-700 text-white text-sm mt-2"
                />
              )}
            </div>

            {/* Section Visibility Toggles */}
            <div className="p-4 bg-zinc-900/60 border border-zinc-800 rounded-lg space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Layers className="h-4 w-4 text-navigate-gold" />
                <Label className="text-white text-sm font-medium">Visible Landing Page Sections</Label>
              </div>

              <div className="space-y-2">
                <label className="flex items-center justify-between p-2 rounded hover:bg-zinc-800/40 cursor-pointer">
                  <span className="text-sm text-zinc-300">Featured Courses</span>
                  <Switch
                    checked={landingPage.show_courses !== false}
                    onCheckedChange={(val) => setLandingPage(prev => ({ ...prev, show_courses: val }))}
                  />
                </label>

                <label className="flex items-center justify-between p-2 rounded hover:bg-zinc-800/40 cursor-pointer">
                  <span className="text-sm text-zinc-300">Interactive Learning Labs</span>
                  <Switch
                    checked={landingPage.show_labs !== false}
                    onCheckedChange={(val) => setLandingPage(prev => ({ ...prev, show_labs: val }))}
                  />
                </label>

                <label className="flex items-center justify-between p-2 rounded hover:bg-zinc-800/40 cursor-pointer">
                  <span className="text-sm text-zinc-300">Learning Pathways</span>
                  <Switch
                    checked={landingPage.show_pathways !== false}
                    onCheckedChange={(val) => setLandingPage(prev => ({ ...prev, show_pathways: val }))}
                  />
                </label>

                <label className="flex items-center justify-between p-2 rounded hover:bg-zinc-800/40 cursor-pointer">
                  <span className="text-sm text-zinc-300">Enterprise Capabilities & Features</span>
                  <Switch
                    checked={landingPage.show_features !== false}
                    onCheckedChange={(val) => setLandingPage(prev => ({ ...prev, show_features: val }))}
                  />
                </label>

                <label className="flex items-center justify-between p-2 rounded hover:bg-zinc-800/40 cursor-pointer">
                  <span className="text-sm text-zinc-300">Platform Statistics & Counter</span>
                  <Switch
                    checked={landingPage.show_stats !== false}
                    onCheckedChange={(val) => setLandingPage(prev => ({ ...prev, show_stats: val }))}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
